import { createContext, FunctionComponent, PropsWithChildren, useContext } from 'react';
import {
  ASSET_REPORT_INITIAL_STATE,
  DATA_REPORT_INITIAL_STATE,
  PDF_REPORT_INITAL_STATE,
  PDF_SITE_STATUS_REPORT_INITIAL_STATE,
} from '../config/config';
import _ from 'lodash';
import { FormDataType, ReportType } from '../types';
import { useTimezone } from '../hooks/useTimezone';
import { useFilterSites } from './sitesContext';
import { useBlockNavigation } from 'src/routes/elements/ReportFlow/BuildReportsElement/blockNavigationContext';
import { omitDesignImageUrls } from '../utils/templateImages';
import type { SavedTemplate } from '../hooks/useTemplateQuery';

const templateContext = createContext<{
  undoChanges: () => void;
  assetProperties: any[];
  checkTemplateInitialState: (state: any) => boolean;
  checkTemplateForChanges: (state: any) => boolean | 'INVALID';
  isSaved: () => boolean;
  onTemplateSaved: (saved: SavedTemplate) => void;
}>({
  assetProperties: [],
  undoChanges: () => {},
  checkTemplateInitialState: () => false,
  checkTemplateForChanges: () => false,
  isSaved: () => false,
  onTemplateSaved: () => {},
});

export const TemplateContextProvider: FunctionComponent<
  PropsWithChildren<{
    reportType: FormDataType | string;
    title: string;
    template: any;
    loaded: boolean;
    undoChanges: (sites?: any) => void;
    assetProperties: any[];
    onTemplateSaved: (saved: SavedTemplate) => void;
  }>
> = ({
  reportType,
  loaded,
  template,
  undoChanges,
  assetProperties,
  onTemplateSaved,
  children,
  title,
}) => {
  const { parseTimezone } = useTimezone();
  const { initialFilters } = useFilterSites();
  const blockNavigation = useBlockNavigation();
  const checkTemplateInitialState = (state: any) => {
    const stateToCompare = _.cloneDeep(state);

    let defaultState;

    if (reportType === 'ASSET') {
      defaultState = ASSET_REPORT_INITIAL_STATE.data;
    } else if (reportType === 'PDF') {
      defaultState = PDF_REPORT_INITAL_STATE.data;
    } else if (reportType === ReportType.PDF_SITE_STATUS_REPORT) {
      defaultState = PDF_SITE_STATUS_REPORT_INITIAL_STATE.data;
    } else {
      defaultState = DATA_REPORT_INITIAL_STATE.data;
    }

    if (typeof stateToCompare.data.filters === 'undefined') delete stateToCompare.data.filters;

    if (_.isEqual(stateToCompare.data, defaultState)) return true;

    return false;
  };
  const isSaved = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const templateId = urlParams.get('templateId');
    return !!templateId;
  };
  // TODO: maybe refactor this
  const checkTemplateForChanges = (state: any) => {
    if (!loaded) return true;
    const stateToCompare = _.cloneDeep(state);

    let defaultState;

    if (reportType === 'ASSET') {
      defaultState = ASSET_REPORT_INITIAL_STATE.data;
    } else if (reportType === 'PDF') {
      defaultState = PDF_REPORT_INITAL_STATE.data;
    } else if (reportType === ReportType.PDF_SITE_STATUS_REPORT) {
      defaultState = PDF_REPORT_INITAL_STATE.data;
    } else {
      defaultState = DATA_REPORT_INITIAL_STATE.data;
    }
    if (typeof stateToCompare.filters === 'undefined') delete stateToCompare.filters;

    if (checkTemplateInitialState(state)) {
      if (blockNavigation) {
        blockNavigation[1](false);
      }
      return 'INVALID';
    }

    const templateToCompare = _.cloneDeep(template || defaultState);
    const templateStateToCompare = templateToCompare.state;
    if (typeof templateToCompare.state.filters === 'undefined')
      delete templateToCompare.state.filters;
    if (typeof stateToCompare.data.filters === 'undefined') delete stateToCompare.data.filters;
    // Normalize custom columns before comparison. `id` is a client-only uuid,
    // `tag.rawInputName` is never persisted to the server, and the top-level `unit`
    // is not an independently editable field (it mirrors the tag on round-trip).
    // Leaving them in makes a freshly-saved template always look "changed".
    const normalizeCustomColumn = (column: any) => {
      delete column.id;
      delete column.unit;
      if (column.tag) delete column.tag.rawInputName;
      return column;
    };
    if (templateStateToCompare?.columns?.custom) {
      templateStateToCompare.columns.custom.forEach(normalizeCustomColumn);
    }
    if (stateToCompare?.data?.columns?.custom) {
      stateToCompare.data.columns.custom.forEach(normalizeCustomColumn);
    }
    // Image URLs are preview-only (a local blob after upload, a signed URL from the GET) and
    // never saved; the image keys are what identify a change.
    if (templateStateToCompare?.design) {
      templateStateToCompare.design = omitDesignImageUrls(templateStateToCompare.design);
    }
    if (stateToCompare?.data?.design) {
      stateToCompare.data.design = omitDesignImageUrls(stateToCompare.data.design);
    }

    let templateTimezone = template.timezone;
    if (typeof templateTimezone === 'string') {
      templateTimezone = parseTimezone(templateTimezone);
    }
    let sameFilters = true;
    if (reportType === 'ASSET') {
      sameFilters = _.isEqual(stateToCompare.filters, initialFilters);
      delete stateToCompare.data.filters;
      delete templateStateToCompare.filters;
      if (
        templateToCompare.state.filters?.assetFilters &&
        !templateToCompare.state.filters.assetFilters.assetTypes
      )
        templateToCompare.state.filters.assetFilters.assetTypes = undefined;
      if (
        templateToCompare.state.filters?.siteFilters &&
        !templateToCompare.state.filters.siteFilters.siteTypes
      )
        templateToCompare.state.filters.siteFilters.siteTypes = undefined;
      if (
        stateToCompare.data.filters?.assetFilters &&
        !stateToCompare.data.filters.assetFilters.assetTypes
      )
        stateToCompare.data.filters.assetFilters.assetTypes = undefined;
      if (
        stateToCompare.data.filters?.siteFilters &&
        !stateToCompare.data.filters.siteFilters.siteTypes
      )
        stateToCompare.data.filters.siteFilters.siteTypes = undefined;
    }

    const changed =
      _.isEqual(
        _.omit(stateToCompare.data, 'timeZone'),
        _.omit(templateStateToCompare, 'timeZone')
      ) &&
      sameFilters &&
      stateToCompare.data.timeZone?.value === templateTimezone?.value &&
      title === template.title;
    if (blockNavigation) {
      blockNavigation[1](!changed);
    }

    return changed;
  };

  return (
    <templateContext.Provider
      value={{
        assetProperties,
        undoChanges,
        checkTemplateForChanges,
        checkTemplateInitialState,
        isSaved,
        onTemplateSaved,
      }}
    >
      {children}
    </templateContext.Provider>
  );
};

export const useTemplateState = () => useContext(templateContext);
